import axios from 'axios';
import { format } from 'date-fns';
import _ from 'lodash';
import { RunModel } from 'models/RunModel/RunModel';
import { Area, ICloudStorage } from 'services/Database/CloudStorageService';
import { SERVER_URL_ALPHA } from 'util/constants';
import { ST } from '../../types';
import { getAuth } from '../../util';
import {
  APIResponse,
  DongleBindResponse,
  DongleStatusResponse,
  EstimatorInterface,
  KeyBindResponse,
  KeyStatusResponse,
} from './Estimator';
import { appData } from 'models/AppData';

export const dateToRecordId = (d: Date | number = new Date()) => {
  return format(d, 'yyyy_MM_dd_HH_mm_ss');
};

const config = {
  headers: {
    Authorization: 'string',
    'content-type': 'application/json',
    pragma: 'no-cache',
    'cache-control': 'no-cache, no-store',
  },
};

// TODO combine this with ICloudStorage. Their functionality got merged over time..
class OnlineAPI implements EstimatorInterface {
  private storage: ICloudStorage;
  private defaultArea: Area;

  constructor(cloudStorage: ICloudStorage, defaultArea: Area) {
    this.storage = cloudStorage;
    this.defaultArea = defaultArea;
  }
  destroy() {}

  setDefaultArea(area: Area) {
    this.defaultArea = area;
  }

  private async getConfig() {
    const user = getAuth().currentUser;
    if (!user) return null;

    const userToken = await user?.getIdToken();
    const uConfig = _.cloneDeep(config);

    uConfig.headers = {
      ...uConfig.headers,
      Authorization: `Bearer ${userToken}`,
    };
    return uConfig;
  }

  async submitRecordData(
    record: RunModel,
    recordId?: string
  ): Promise<ST.Play> {
    const user = getAuth().currentUser;
    const uConfig = await this.getConfig();

    if (!user || !uConfig) {
      throw new Error(`User info not available for upload.`);
    }
    
    // upload record data to firebase
    const recordURL = await this.storage.uploadRecord(record.recordData!);
    // Wait a bit to make sure the record is uploaded
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Fix: recordId muss gesetzt sein!
    const validRecordId = recordId ?? record.recordId;
    if (!validRecordId) {
      throw new Error('No valid recordId provided for processing!');
    }

    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const processUrl = `${serverUrl}/v_1/Record/process/${this.defaultArea}/${user.uid}/${record.deviceId}/${validRecordId}`;

    await axios.post(processUrl, {}, uConfig);

    record.setPlayURL(recordURL.replaceAll('Record', 'Play'));
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await record.downloadPlayData();
    return record.playData!;
  }

  private checkCache: Record<string, [number, KeyStatusResponse]> = {};
  async checkKeyStatus(key: string): Promise<KeyStatusResponse> {
    const now = Date.now();
    const cached = this.checkCache[key];
    if (cached && now - cached[0] < 10000) {
      // cache is only 10s valid
      return cached[1];
    }
    const uConfig = await this.getConfig();
    if (!uConfig) {
      throw new Error(`User info not available for key status check.`);
    }
    // Dynamische Server-URL aus Settings holen
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const statusUrl = `${serverUrl}/v_1/Shop/license/${key}/status`;
    const response = await axios.get<KeyStatusResponse>(statusUrl, uConfig);
    this.checkCache[key] = [now, response.data];
    return response.data;
  }

  async bindKey(key: string): Promise<KeyBindResponse> {
    const uConfig = await this.getConfig();
    const user = getAuth().currentUser;
    if (!uConfig || !user) {
      throw new Error(`User info not available for binding key.`);
    }
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const statusUrl = `${serverUrl}/v_1/Shop/license/${key}/bind/${user.uid}`;
    const response = await axios.post<KeyBindResponse>(statusUrl, {}, uConfig);
    return response.data;
  }

  private dongleCheckCache: Record<string, [number, DongleStatusResponse]> = {};
  async checkDongleStatus(mac: string): Promise<DongleStatusResponse> {
    const now = Date.now();
    const cached = this.dongleCheckCache[mac];
    if (cached && now - cached[0] < 10000) {
      // cache is only 10s valid
      return cached[1];
    }
    const uConfig = await this.getConfig();
    if (!uConfig) {
      throw new Error(`User info not available for key status check.`);
    }
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const statusUrl = `${serverUrl}/v_1/Shop/dongle/${mac}/status`;
    const response = await axios.get<DongleStatusResponse>(statusUrl, uConfig);
    this.dongleCheckCache[mac] = [now, response.data];
    return response.data;
  }

  async bindDongle(mac: string): Promise<DongleBindResponse> {
    const uConfig = await this.getConfig();
    const user = getAuth().currentUser;
    if (!uConfig || !user) {
      throw new Error(`User info not available for binding key.`);
    }
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const statusUrl = `${serverUrl}/v_1/Shop/dongle/${mac}/bind/${user.uid}`;
    const response = await axios.post<DongleBindResponse>(
      statusUrl,
      {},
      uConfig
    );
    return response.data;
  }

  async deleteUser(): Promise<APIResponse> {
    const uConfig = await this.getConfig();
    const user = getAuth().currentUser;
    if (!uConfig || !user) {
      throw new Error(`User info not available for deleting user.`);
    }
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const deleteUrl = `${serverUrl}/firebase/user`;
    const response = await axios.delete<APIResponse>(deleteUrl, uConfig);
    return response.data;
  }

  async deleteRun(
    deviceId: string,
    recordId: string,
    area?: Area
  ): Promise<APIResponse> {
    const uConfig = await this.getConfig();
    const user = getAuth().currentUser;
    if (!uConfig || !user) {
      throw new Error(`User info not available for deleting user.`);
    }
    const theArea = area ?? this.defaultArea;
    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;
    const deleteUrl = `${serverUrl}/firebase/data/${theArea}/${user.uid}/${deviceId}/${recordId}`;
    const response = await axios.delete<APIResponse>(deleteUrl, uConfig);
    return response.data;
  }
}

export default OnlineAPI;
